import { useState, useEffect } from "react";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { useIntersectionObserver } from "../hooks/useIntersectionObserver";
import {
  ChevronRight,
  ArrowRight,
  Users,
  FolderOpen,
  Bot,
  Shield,
  FileEdit,
  Brain,
  ShieldCheck,
  CalendarDays,
  FolderGit2,
  MessagesSquare,
  Workflow,
  Palette,
  KanbanSquare,
  Lock,
  Zap,
  Sparkles,
  ListTodo,
  BarChart3,
  Link2,
  Activity,
  LayoutDashboard,
  SlidersHorizontal,
} from "lucide-react";
// Additional icons for Notes section
import { Image, Filter, Tag, ScanText, NotebookPen, SparkleIcon } from "lucide-react";
// Icons for Mail section
import { Mail, Inbox, Send } from "lucide-react";

import googleLogo from "../assets/images/logos/google.svg";
import microsoftLogo from "../assets/images/logos/microsoft.svg";
import slackLogo from "../assets/images/logos/slack.svg";
import trelloLogo from "../assets/images/logos/trello.svg";
import githubLogo from "../assets/images/logos/github.svg";
import asanaLogo from "../assets/images/logos/asana.svg";
import zapierLogo from "../assets/images/logos/zapier.svg";
import dropboxLogo from "../assets/images/logos/dropbox.svg";

const FeatureCard = ({ feature, darkMode, index }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`p-8 rounded-2xl shadow-lg transition-all duration-500 hover:shadow-2xl group relative overflow-hidden ${
        darkMode ? "bg-gray-800" : "bg-white"
      } ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-600 to-purple-600 transform origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></div>
      <div className="flex items-center mb-6">
        <div
          className={`text-4xl rounded-full w-16 h-16 flex items-center justify-center mr-4 transition-all duration-300 group-hover:scale-110 ${
            darkMode ? "bg-indigo-900" : "bg-indigo-100"
          }`}
        >
          {feature.icon}
        </div>
        <h3
          className={`text-xl font-semibold transition-opacity duration-300 ${
            darkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          {feature.title}
        </h3>
      </div>
      <p
        className={`transition-opacity duration-300 ${
          darkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        {feature.description}
      </p>
    </div>
  );
};

function FeaturesPage() {
  const [darkMode, setDarkMode] = useState(false);
  const [logoFade, setLogoFade] = useState(false);

  const integrations = [
    { name: "Google", logo: googleLogo },
    { name: "Microsoft", logo: microsoftLogo },
    { name: "Slack", logo: slackLogo },
    { name: "Trello", logo: trelloLogo },
    { name: "GitHub", logo: githubLogo },
    { name: "Asana", logo: asanaLogo },
    { name: "Zapier", logo: zapierLogo },
    { name: "Dropbox", logo: dropboxLogo },
  ];

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const handleDarkModeToggle = () => {
    setLogoFade(true);
    setTimeout(() => {
      setDarkMode(!darkMode);
      setLogoFade(false);
    }, 150);
  };

  return (
    <main className="flex-grow overflow-y-auto">
      <NavBar darkMode={darkMode} logoFade={logoFade} />

      {/* Hero Section */}
      <section className="relative pt-[160px]">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              Features
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Powerful Features for{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Modern Work
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Discover how Boxia's cutting-edge features can revolutionize your
              workflow
            </p>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: KanbanSquare,
                title: "Task Management",
                description: "AI-powered task organization and prioritization",
                features: [
                  "Smart task prioritization",
                  "Custom workflows",
                  "Team collaboration",
                  "Progress tracking",
                ],
              },
              {
                icon: FileEdit,
                title: "Note Taking",
                description: "Intelligent note organization and collaboration",
                features: [
                  "Rich text editing",
                  "File attachments",
                  "Version history",
                  "Collaborative editing",
                ],
              },
              {
                icon: CalendarDays,
                title: "Calendar",
                description: "AI-optimized scheduling and time management",
                features: [
                  "Smart scheduling",
                  "Meeting optimization",
                  "Time blocking",
                  "Team availability",
                ],
              },
              {
                icon: FolderGit2,
                title: "File Storage",
                description: "Unified, intelligent file management system",
                features: [
                  "Cloud storage",
                  "File sharing",
                  "Version control",
                  "Smart organization",
                ],
              },
              {
                icon: MessagesSquare,
                title: "Team Chat",
                description: "Real-time communication and collaboration",
                features: [
                  "Direct messaging",
                  "Group channels",
                  "File sharing",
                  "Thread discussions",
                ],
              },
              {
                icon: Brain,
                title: "AI Assistant",
                description: "Intelligent productivity enhancement",
                features: [
                  "Smart suggestions",
                  "Task automation",
                  "Content generation",
                  "Workflow optimization",
                ],
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                    <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {feature.features.map((subFeature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                      {subFeature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Boxia Kairo Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-6 max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Meet{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Kairo
              </span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              Your AI workspace assistant that actually gets work done. Ask Kairo to schedule meetings, organize tasks, write emails, or create content, and watch it happen across your entire workspace instantly.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14">
            {[
              {
                icon: SparkleIcon,
                title: "Truly Intelligent",
                description:
                  "Kairo doesn't just chat. It plans, executes, and follows through on complex work across your entire workspace.",
                features: [
                  "Understands your full workspace",
                  "Handles multi-step projects",
                  "Learns your workflow patterns",
                  "Works autonomously when needed",
                ],
              },
              {
                icon: Link2,
                title: "Deeply Integrated",
                description:
                  "One conversation can update tasks, schedule meetings, send emails, organize notes, and manage your boards. No switching, no copying.",
                features: [
                  "Manages tasks and boards",
                  "Schedules and updates calendar",
                  "Drafts and sends emails",
                  "Organizes files and notes",
                ],
              },
              {
                icon: SlidersHorizontal,
                title: "Your Choice of AI",
                description:
                  "Choose the AI model that fits your task. Compare different models side by side, or let Kairo pick the best one for you.",
                features: [
                  "Access to leading AI models",
                  "Switch models anytime",
                  "Compare responses instantly",
                  "Adjust depth and speed",
                ],
              },
            ].map((mode, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 p-6 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                    <mode.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {mode.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {mode.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {mode.features.map((f, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Kairo brings everything together. Start a conversation and end with scheduled meetings, updated tasks, triaged emails, and organized notes. All without leaving your workspace or switching between apps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Boxia Tab Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-6 max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Boxia{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Tab
              </span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              The world's fastest and most accurate AI
              autocomplete—purpose‑built for operational work, knowledge flow,
              and team execution. Tab anticipates what you need next in tasks,
              notes, messages, queries, and automations—so you produce more with
              radically less friction.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {[
              {
                icon: Zap,
                title: "Blazing Speed",
                description:
                  "Inference tuned for sub‑100ms feel inside collaborative surfaces.",
                features: [
                  "Ultra-low latency",
                  "Streaming tokens",
                  "Edge caching",
                  "Adaptive prefetch",
                ],
              },
              {
                icon: Sparkles,
                title: "Precision Context",
                description:
                  "Understands task state, project goals & recent activity to stay relevant.",
                features: [
                  "Structured context graph",
                  "Semantic grounding",
                  "Noise filtering",
                  "Role awareness",
                ],
              },
              {
                icon: Brain,
                title: "Predictive Future",
                description:
                  "Upcoming: predictive model that suggests your next action before you ask.",
                features: [
                  "Action prediction (coming)",
                  "Flow continuation",
                  "Multi-surface hints",
                  "Safe overrides",
                ],
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 p-6 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                    <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {feature.features.map((subFeature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                      {subFeature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Boxia Tab works hand‑in‑hand with Kairo—what Kairo understands,
                Tab can accelerate. Together they compress the distance between
                intent and impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-16 md:px-8">
          {/* Header + Side Detail */}
          <div className="grid md:grid-cols-5 gap-12 mb-20 items-start">
            <div className="md:col-span-3 space-y-6 text-center md:text-left">
              <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
                Calendar
              </h2>
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-xl">
                A calm, intelligent calendar that bends to you. Describe an
                event in plain language—Kairo instantly drafts it with time
                zones, guests, location, and smart suggestions. Layer school,
                personal, and team schedules to see the whole picture without
                the noise. Two‑way sync with Google Calendar and Microsoft
                Outlook keeps everything updated instantly—no double entry, no
                context switching, no falling out of sync.
              </p>
              {/* Integration Tags */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-start pt-2">
                {[
                  "Real‑time Two‑Way Sync",
                  "Google Calendar",
                  "Microsoft Outlook",
                  "Automatic Conflict Detection",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/30 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 text-gray-700 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="md:col-span-2 space-y-5">
              <div className="rounded-xl border border-black/5 dark:border-white/5 p-5 bg-gradient-to-tr from-zinc-300/25 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2 text-sm">
                  Plain Language → Event
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  “Lunch sync with design leads next Tue 12-12:45, add Figma
                  link” becomes a structured, share‑ready event with attachments
                  & channel notes in under a second.
                </p>
              </div>
              <div className="rounded-xl border border-black/5 dark:border-white/5 p-5 bg-gradient-to-tr from-zinc-300/25 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2 text-sm">
                  Adaptive Focus Windows
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Kairo spots fragmented afternoons and suggests reclaimable
                  focus blocks—auto‑protecting them if you accept.
                </p>
              </div>
            </div>
          </div>

          {/* Feature Cards Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              {
                icon: CalendarDays,
                title: "Natural Input",
                description: "Type it like you’d say it.",
                bullets: [
                  "Plain phrasing",
                  "Smart parsing",
                  "Timezone aware",
                  "Instant preview",
                ],
              },
              {
                icon: Sparkles,
                title: "Kairo Suggestions",
                description: "Better timing & spacing.",
                bullets: [
                  "Conflict alerts",
                  "Travel buffer",
                  "Focus detection",
                  "Smart reminders",
                ],
              },
              {
                icon: Users,
                title: "Layers & Sharing",
                description: "Blend what matters.",
                bullets: [
                  "School + personal",
                  "Team visibility",
                  "Google + Microsoft sync",
                  "Availability links",
                ],
              },
              {
                icon: Palette,
                title: "Custom Views",
                description: "See time your way.",
                bullets: [
                  "Day / week / agenda",
                  "Focus strip",
                  "Color themes",
                  "Keyboard first",
                ],
              },
            ].map((card, i) => (
              <div
                key={i}
                className="rounded-xl border-[1px] border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 group hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-lg p-3 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20">
                    <card.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {card.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {card.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {card.bullets.map((b, idx) => (
                    <li
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Deep Integration Copy */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Calendar isn’t a silo. Attach docs, surface relevant tasks
                inside the event pane, open quick notes that sync back to the
                meeting, and let Kairo summarize & push follow‑ups. Your
                schedule becomes an active command surface—not a static grid.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tasks Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 md:px-8">
          {/* Centered Hero */}
          <div className="max-w-3xl mx-auto text-center mb-20 space-y-6">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Tasks
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              A radically modern task system that thinks with you. Capture
              anything anywhere, let Kairo shape it into structured work, watch
              priorities adapt live, and surface analytics that guide
              momentum—not just tally output.
            </p>
            <div className="flex gap-3 overflow-x-auto no-scrollbar justify-start md:justify-center pt-1 pb-2 -mx-4 px-4 snap-x">
              {[
                "Natural Language",
                "AI Priorities",
                "Google Tasks",
                "Microsoft To Do",
                "Analytics",
                "Automations",
              ].map((pill) => (
                <span
                  key={pill}
                  className="snap-start whitespace-nowrap text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/30 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 text-gray-700 dark:text-gray-300"
                >
                  {pill}
                </span>
              ))}
            </div>
          </div>

          {/* Asymmetric Grid */}
          <div className="grid lg:grid-cols-5 gap-8 mb-20">
            {/* Left Column (2 cols span) */}
            <div className="space-y-8 lg:col-span-2">
              <div className="rounded-2xl h-full border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/25 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 flex flex-col justify-between">
                <div className="space-y-5">
                  <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 text-gray-700 dark:text-gray-300">
                    <ListTodo className="w-4 h-4" /> Unified Capture
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight">
                    Capture anywhere. It all lands clean.
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    Slash commands, quick‑add palette, inline from notes or
                    chat—every input is parsed by Kairo for owners, dates,
                    intent, and relationships. No more half‑formed todos that
                    decay in random lists.
                  </p>
                </div>
                <ul className="mt-6 grid grid-cols-2 gap-3 text-xs">
                  {[
                    "Slash quick‑add",
                    "Drag from text",
                    "Smart parsing",
                    "Instant linking",
                  ].map((b) => (
                    <li
                      key={b}
                      className="flex items-center text-gray-600 dark:text-gray-400"
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Middle Spotlight (2 cols span) */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div className="relative rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-purple-500/10 via-fuchsia-400/10 to-transparent dark:from-purple-500/20 dark:via-fuchsia-400/10 overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-purple-500/10 blur-2xl" />
                <div className="flex items-center gap-2 text-xs font-medium mb-4 px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 w-fit">
                  <Activity className="w-4 h-4" /> Analytics & Flow
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight mb-4">
                  Understand effort, drift & momentum.
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  See completion velocity, aging tasks, focus allocation, and
                  emerging bottlenecks. Insight panels convert raw volume into
                  directional signals you can act on now.
                </p>
                <ul className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    "Cycle time trend",
                    "Age alerts",
                    "Focus vs. churn",
                    "Load heatmap",
                  ].map((i) => (
                    <li
                      key={i}
                      className="flex items-center text-gray-600 dark:text-gray-400"
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                  <Workflow className="w-4 h-4" /> Adaptive Priority
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Dynamic ranking reacts to shifting calendars, blockers, and
                  completed dependencies—your focus lane stays lean
                  automatically.
                </p>
              </div>
            </div>

            {/* Right Column (1 col span) */}
            <div className="space-y-8 lg:col-span-1">
              <div className="rounded-2xl border border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                  <ListTodo className="w-4 h-4" /> Quick Input
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Global shortcut opens a frictionless command bar—type intent,
                  press enter, done.
                </p>
              </div>
              <div className="rounded-2xl border border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                  <Link2 className="w-4 h-4" /> Deep Links
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Tasks link natively to notes, files, calendar blocks &
                  chats—context is one click away.
                </p>
              </div>
              <div className="rounded-2xl border border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" /> Smart Subtasks
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Convert a vague idea into an actionable breakdown with one
                  Kairo prompt.
                </p>
              </div>
            </div>
          </div>

          {/* Knowledge Graph Copy */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Every task is a node in a living graph—tied to the meetings that
                spawned it, the notes that refine it, the files that enable it,
                and the decisions that close it. Kairo traverses this graph to
                surface only the context that sharpens execution—nothing noisy,
                nothing stale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notes Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-6 max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Notes
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              Boxia Notes is a living canvas for thinking, studying, planning
              and sharing. Rich text, AI acceleration and deep knowledge graph
              context make every note more useful the moment you create it.
              Rapid capture, powerful structure—without friction.
            </p>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide justify-start md:justify-center pt-1 pb-2 -mx-4 px-4 snap-x">
              {[
                "Rich Text Editing",
                "Google Keep Import",
                "Boxia Tab Inline",
                "Image → Note AI",
                "Filters & Graph",
                "Templates",
                "Bi‑directional Links",
              ].map((pill) => (
                <span
                  key={pill}
                  className="snap-start whitespace-nowrap text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/30 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 text-gray-700 dark:text-gray-300"
                >
                  {pill}
                </span>
              ))}
            </div>
          </div>
          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {[
              {
                icon: NotebookPen,
                title: "Fluid, Expressive Writing",
                description:
                  "Type, slash-command, or summon Tab for next sentence—stay in flow.",
                bullets: [
                  "Inline slash palette",
                  "Markdown shortcuts",
                  "Mention anything",
                  "Real‑time sync",
                ],
              },
              {
                icon: Sparkles,
                title: "AI That Co‑Writes",
                description:
                  "Boxia Tab anticipates headings, summaries & action items.",
                bullets: [
                  "Context aware",
                  "Auto summaries",
                  "Tone shifts",
                  "Rewrite & expand",
                ],
              },
              {
                icon: ScanText,
                title: "Image → Structured Note",
                description:
                  "Snap a whiteboard or page—Kairo extracts, cleans & organizes.",
                bullets: [
                  "OCR + cleanup",
                  "Key points",
                  "Action extraction",
                  "Source link",
                ],
              },
              {
                icon: Filter,
                title: "Organize at Scale",
                description:
                  "Graph links, smart filters & tags keep knowledge alive.",
                bullets: [
                  "Tag & multi-tag",
                  "Bi‑directional links",
                  "Graph suggestions",
                  "Saved views",
                ],
              },
            ].map((card, i) => (
              <div
                key={i}
                className="rounded-xl border-[1px] border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 group hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-lg p-3 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20">
                    <card.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {card.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {card.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {card.bullets.map((b, idx) => (
                    <li
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {/* Deep Copy Panels */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-purple-500/10 via-fuchsia-400/10 to-transparent dark:from-purple-500/20 dark:via-fuchsia-400/10 overflow-hidden relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-purple-500/10 blur-2xl" />
              <div className="inline-flex items-center gap-2 text-xs font-medium mb-4 px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 w-fit">
                <Image className="w-4 h-4" /> Quick Note from Image
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight mb-4">
                Turn any capture into clarity.
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Drop a screenshot, lecture slide or whiteboard photo. Kairo
                performs high‑accuracy OCR, removes noise & seating chatter,
                groups concepts, produces an outline plus optional summary &
                next steps. One click to link resulting tasks.
              </p>
              <ul className="grid grid-cols-2 gap-3 text-xs">
                {[
                  "Outline generated",
                  "Action items",
                  "Tag suggestions",
                  "Source thumb",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 flex flex-col justify-between">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 text-gray-700 dark:text-gray-300">
                  <Tag className="w-4 h-4" /> Smart Structure
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight">
                  Structure emerges as you write.
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Kairo auto‑suggests tags, backlinks and related notes as
                  context shifts. Filters become predictive—pin a saved view for
                  exams, project retros or research threads.
                </p>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-3 text-xs">
                {[
                  "Graph traversal",
                  "Related surfacing",
                  "Duplicate detection",
                  "Auto backlinks",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-center text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                <Link2 className="w-4 h-4" /> Keep → Boxia
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Import your Google Keep notes in one sweep—preserving lists,
                images & checkboxes. They become first‑class Boxia notes with
                richer editing, graph linking and AI acceleration. Continue
                exactly where you left off, just more powerful.
              </p>
            </div>
          </div>
          {/* Closing Copy */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Notes aren’t static documents—they’re living nodes connected to
                tasks, events, files and decisions. Boxia’s AI builds memory
                across them, so every fresh idea starts already grounded in what
                you (and your team) know.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Boards Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-6 max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Boards
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              Beyond basic kanban. Boxia Boards fuse multi‑view project
              modeling, real‑time multiplayer, adaptive AI and deep
              cross‑surface context. Switch from kanban to timeline to swimlane
              without losing state. Kairo proactively spots risk, refactors
              scope and accelerates grooming so teams ship cleaner, faster,
              calmer.
            </p>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide justify-start md:justify-center pt-1 pb-2 -mx-4 px-4 snap-x">
              {[
                "Kanban+",
                "Timeline",
                "Swimlanes",
                "AI Backlog Refinement",
                "Realtime Presence",
                "Automations",
                "Backlog Intelligence",
                "Metrics",
              ].map((pill) => (
                <span
                  key={pill}
                  className="snap-start whitespace-nowrap text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/30 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 text-gray-700 dark:text-gray-300"
                >
                  {pill}
                </span>
              ))}
            </div>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {[
              {
                icon: KanbanSquare,
                title: "Flexible Views",
                description: "Kanban, timeline, grouped lanes & focus queues.",
                bullets: [
                  "One data model",
                  "Instant view swap",
                  "Sticky filters",
                  "Inline resizing",
                ],
              },
              {
                icon: Sparkles,
                title: "AI Backlog Refinement",
                description:
                  "Kairo clusters, dedupes & clarifies stories automatically.",
                bullets: [
                  "Story rewrite",
                  "Estimate assist",
                  "Duplicate merge",
                  "Scope suggestions",
                ],
              },
              {
                icon: Activity,
                title: "Flow Intelligence",
                description: "Signals highlight drift & risk early.",
                bullets: [
                  "Stale lane alerts",
                  "Blocked detection",
                  "WIP pressure",
                  "Throughput trend",
                ],
              },
              {
                icon: Users,
                title: "Real‑Time Presence",
                description: "Multiplayer editing & cursor awareness.",
                bullets: [
                  "Live cursors",
                  "Inline comments",
                  "Role badges",
                  "Near zero latency",
                ],
              },
            ].map((card, i) => (
              <div
                key={i}
                className="rounded-xl border-[1px] border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 group hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-lg p-3 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20">
                    <card.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {card.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {card.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {card.bullets.map((b, idx) => (
                    <li
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Deep Panels */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-purple-500/10 via-fuchsia-400/10 to-transparent dark:from-purple-500/20 dark:via-fuchsia-400/10 overflow-hidden relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-purple-500/10 blur-2xl" />
              <div className="inline-flex items-center gap-2 text-xs font-medium mb-4 px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 w-fit">
                <Workflow className="w-4 h-4" /> Adaptive Automation
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight mb-4">
                Less manual grooming, more momentum.
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Auto‑advance rules, SLA timers, label suggestion and dependency
                awareness keep boards clean. Kairo rewrites vague cards,
                extracts subtasks and flags risky sequencing before it burns
                time.
              </p>
              <ul className="grid grid-cols-2 gap-3 text-xs">
                {[
                  "Auto transitions",
                  "Dependency watch",
                  "Estimate assist",
                  "Risk surfacing",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 flex flex-col justify-between">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 text-gray-700 dark:text-gray-300">
                  <LayoutDashboard className="w-4 h-4" /> Multi‑View Cohesion
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight">
                  One source, many lenses.
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  Switch kanban ⇄ timeline ⇄ swimlane without rebuilding columns
                  or filters. Saved perspectives power standups, release
                  planning and incident reviews—always live, never copied.
                </p>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-3 text-xs">
                {[
                  "Dynamic grouping",
                  "View presets",
                  "Focus slices",
                  "Share links",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-center text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                <Brain className="w-4 h-4" /> Predictive Flow
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Kairo forecasts completion windows by blending historical
                velocity, current calendar load and dependency graph weight. It
                suggests sequence adjustments that unlock earlier impact with
                less crunch.
              </p>
            </div>
          </div>

          {/* Closing Copy */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Boards connect seamlessly with tasks, notes, calendar and
                files—every card is a living junction. AI keeps work flowing
                forward instead of fossilizing in columns.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Files Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-6 max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Files
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              A calm, modern home for every file in your life—class notes,
              design assets, research PDFs, recordings, decks, team folders.
              Everyone gets 50GB free. Upgrade for effectively unlimited* space
              so you never have to play cleanup or sacrifice quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {[
              {
                icon: FolderOpen,
                title: "Space You Can Breathe In",
                description:
                  "50GB free. Upgrade for effectively unlimited* storage.",
                features: [
                  "50GB included",
                  "Room to grow",
                  "Simple upgrade path",
                  "No manual cleanup",
                ],
              },
              {
                icon: Zap,
                title: "Fast & Effortless",
                description:
                  "Uploads feel instant—even for big videos & design files.",
                features: [
                  "Smart chunking",
                  "Resume if interrupted",
                  "Snappy previews",
                  "Quick search ready",
                ],
              },
              {
                icon: Users,
                title: "Sharing That Makes Sense",
                description:
                  "Send a link or invite teammates with the right access.",
                features: [
                  "Public / private links",
                  "Expiration & passwords",
                  "Team roles",
                  "Activity at a glance",
                ],
              },
              {
                icon: Sparkles,
                title: "Always Original Quality",
                description:
                  "We never silently compress or downgrade your files.",
                features: [
                  "Full‑resolution storage",
                  "Clean inline previews",
                  "Handles any format",
                  "Polished, simple UI",
                ],
              },
            ].map((block, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 p-6 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                    <block.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {block.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {block.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {block.features.map((f, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                * "Effectively unlimited" means typical personal, academic,
                creative, and team usage won’t hit a hard cap. If you’re storing
                unusually massive datasets or archival footage, reach out and
                we’ll tailor a plan—no surprise lockouts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mail Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-6 max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Mail
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
              The fastest, best‑looking email experience—built directly into
              Boxia. Unified multi‑account inbox, Gmail & Outlook support,
              AI‑accelerated compose and triage, and a razor‑fast viewer that
              makes legacy webmail feel sluggish.
            </p>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide justify-start md:justify-center pt-1 pb-2 -mx-4 px-4 snap-x">
              {[
                "Gmail",
                "Outlook",
                "Multi‑Account",
                "AI Compose",
                "Rewrite & Tone",
                "AI Labels",
                "Unified Inbox",
                "Keyboard‑First",
              ].map((pill) => (
                <span
                  key={pill}
                  className="snap-start whitespace-nowrap text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/30 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 text-gray-700 dark:text-gray-300"
                >
                  {pill}
                </span>
              ))}
            </div>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {[
              {
                icon: Zap,
                title: "Speed & Polish",
                description: "Opens threads instantly—no spinner fatigue.",
                bullets: [
                  "Sub‑100ms feel",
                  "Instant thread load",
                  "Inline media",
                  "Zero clutter UI",
                ],
              },
              {
                icon: Inbox,
                title: "Unified Inbox",
                description: "Multiple Gmail & Outlook accounts together.",
                bullets: [
                  "Multi‑account",
                  "Cross‑account search",
                  "Per‑inbox focus lanes",
                  "Smart collapsing",
                ],
              },
              {
                icon: Sparkles,
                title: "AI Compose + Rewrite",
                description: "Kairo drafts, adapts tone & summarizes threads.",
                bullets: [
                  "Generate reply",
                  "Tone adjust",
                  "Summarize long chains",
                  "One‑click clarify",
                ],
              },
              {
                icon: Filter,
                title: "Intelligent Organization",
                description: "Labels & priority emerge automatically.",
                bullets: [
                  "AI labeling",
                  "Focus triage queue",
                  "Bundle low‑value noise",
                  "Attachment surfacing",
                ],
              },
            ].map((card, i) => (
              <div
                key={i}
                className="rounded-xl border-[1px] border-black/5 dark:border-white/5 p-6 bg-gradient-to-tr from-zinc-300/25 via-gray-400/20 to-transparent dark:from-zinc-300/10 dark:via-gray-400/10 group hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-lg p-3 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20">
                    <card.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-gray-100">
                      {card.title}
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {card.description}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {card.bullets.map((b, idx) => (
                    <li
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Deep Panels */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-purple-500/10 via-fuchsia-400/10 to-transparent dark:from-purple-500/20 dark:via-fuchsia-400/10 overflow-hidden relative">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-purple-500/10 blur-2xl" />
              <div className="inline-flex items-center gap-2 text-xs font-medium mb-4 px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 w-fit">
                <Mail className="w-4 h-4" /> Advanced Compose
              </div>
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight mb-4">
                Write less, send better.
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                Start with a fragment—Kairo expands it with inferred context,
                pulls related tasks or notes, and suggests crisp subject lines.
                Rewrite tone (warmer, tighter, more formal) without losing
                intent.
              </p>
              <ul className="grid grid-cols-2 gap-3 text-xs">
                {[
                  "Reply draft",
                  "Subject suggestions",
                  "Tone shifts",
                  "Inline citations",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10 flex flex-col justify-between">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full border border-black/5 dark:border-white/10 bg-gradient-to-tr from-zinc-300/40 via-purple-400/20 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 text-gray-700 dark:text-gray-300">
                  <Inbox className="w-4 h-4" /> Focus Triage
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 tracking-tight">
                  Focus where it matters.
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  AI bundles low‑value notifications and surfaces high‑leverage
                  threads. One key to archive, defer or jump to linked tasks &
                  files.
                </p>
              </div>
              <ul className="mt-6 grid grid-cols-2 gap-3 text-xs">
                {[
                  "Noise bundling",
                  "Priority rank",
                  "Smart defer",
                  "One‑key actions",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-center text-gray-600 dark:text-gray-400"
                  >
                    <span className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400"></span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/30 via-purple-400/10 to-transparent dark:from-zinc-300/10 dark:via-purple-400/10">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                <Send className="w-4 h-4" /> Gmail & Outlook Sync
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Two‑way, low‑latency sync keeps state perfectly aligned—stars,
                labels, read status and sent mail reflect instantly. No context
                switching tabs; mail is a first‑class citizen alongside tasks &
                notes.
              </p>
            </div>
          </div>

          {/* Closing Copy */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="rounded-2xl border-[1px] border-black/5 dark:border-white/5 p-8 bg-gradient-to-tr from-zinc-300/20 via-purple-400/10 to-transparent dark:from-zinc-300/5 dark:via-purple-400/10">
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                Mail isn’t bolted on—it’s woven into the graph. Drafts pull task
                context, notes link back to threads, attachments preview in
                place, and Kairo unifies communication with execution so nothing
                slips.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Enterprise-Grade{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Security
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: "Data Encryption",
                description:
                  "End-to-end encryption for all your sensitive data",
                features: [
                  "AES-256 encryption",
                  "Zero-knowledge architecture",
                  "Secure key management",
                  "Data-at-rest protection",
                ],
              },
              {
                icon: Lock,
                title: "Access Control",
                description: "Granular permissions and role-based access",
                features: [
                  "Role-based access",
                  "2FA/MFA support",
                  "SSO integration",
                  "Audit logging",
                ],
              },
              {
                icon: ShieldCheck,
                title: "Compliance",
                description: "Meet industry standards and regulations",
                features: [
                  "GDPR compliance",
                  "SOC 2 Type II",
                  "HIPAA ready",
                  "Regular audits",
                ],
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-6 transition-all duration-300 hover:shadow-xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="inline-block rounded-lg bg-gradient-to-tr from-zinc-300/30 via-purple-400/30 to-transparent dark:from-zinc-300/10 dark:via-purple-400/20 p-3">
                    <feature.icon className="w-6 h-6 text-gray-900 dark:text-gray-100" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {feature.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {feature.features.map((subFeature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                    >
                      <div className="mr-2 h-1 w-1 rounded-full bg-purple-600 dark:bg-purple-400" />
                      {subFeature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Showcase */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-5xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Seamless{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Integrations
              </span>
            </h2>
          </div>

          <div className="relative overflow-hidden rounded-xl border-[1px] border-black/5 dark:border-white/5 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {integrations.map((integration, index) => (
                <div
                  key={index}
                  className="flex items-center justify-center group"
                >
                  <img
                    src={integration.logo}
                    alt={`${integration.name} logo`}
                    className="w-auto h-8 opacity-50 group-hover:opacity-100 transition-opacity duration-300 dark:invert dark:brightness-0 dark:contrast-200"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative">
        <div className="absolute inset-0 z-[0] bg-purple-950/10 dark:bg-purple-950/10 bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_20%_80%_at_50%_-20%,rgba(120,119,198,0.3),rgba(255,255,255,0))]" />
        <div className="relative max-w-screen-xl z-10 mx-auto px-4 py-28 gap-12 md:px-8">
          <div className="space-y-5 max-w-3xl mx-auto text-center">
            <h1 className="text-sm text-gray-600 dark:text-gray-400 group font-geist mx-auto px-5 py-2 bg-gradient-to-tr from-zinc-300/20 via-gray-400/20 to-transparent dark:from-zinc-300/5 dark:via-gray-400/5 border-[2px] border-black/5 dark:border-white/5 rounded-xl w-fit">
              Alpha Access
              <ChevronRight className="inline w-4 h-4 ml-2 group-hover:translate-x-1 duration-300" />
            </h1>
            <h2 className="text-4xl tracking-tighter font-geist bg-clip-text text-transparent mx-auto md:text-6xl bg-[linear-gradient(180deg,_#000_0%,_rgba(0,_0,_0,_0.75)_100%)] dark:bg-[linear-gradient(180deg,_#FFF_0%,_rgba(255,_255,_255,_0.00)_202.08%)]">
              Be Part of the Future of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200">
                Work
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              Join the Boxia Alpha and be among the first to experience
              Boxia's revolutionary productivity suite
            </p>

            <div className="mt-10 flex flex-col items-center gap-4">
              <a
                href="https://app.boxia.io/auth/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 text-base font-medium rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 dark:from-purple-300 dark:to-orange-200 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Sign Up Now
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 duration-300" />
              </a>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                 
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer darkMode={darkMode} />
    </main>
  );
}

export default FeaturesPage;
